import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import GooglePlacesDropdown from './GooglePlaces';
import { GetQuery } from './Shared';
import type { GenericList } from './models/JobSharedInterfaces';
import type { AddressDetails } from './models/JobInterfaces';
import type { NewManualItem } from './models/JobScheduleInterfaces';

interface AllocateAddItemProps {
    systemCompanyId: number;
    showNewItemModal: boolean;
    newItem: NewManualItem;
    scheduleTypes: GenericList[],
    itemTypeGroups: GenericList[],
    driverJob: boolean;     //manual job being added from driver system
    hideModal(): void;
    updateData(newItem: NewManualItem): void;
    save(): void;
}

interface AddItemModal {
    jobIdSearch: string;
    siteAddressSearch: string;
    showResults: boolean;
    results: JobSearchResults[];
    googleAddress: AddressDetails;
    siteAddressDoesntExist: boolean;
    errors: any;
    loading: boolean;
}

interface JobSearchResults {
    id: number;
    customer: string;
    siteAddress: string;
    finalQuoteNumber: string;
    status: string;
}

export default class AllocateAddItemModal extends React.Component<AllocateAddItemProps, AddItemModal> {
    constructor(props: AllocateAddItemProps) {
        super(props);
        this.state = {
            jobIdSearch: "",
            siteAddressSearch: "",
            results: [],
            showResults: false,
            googleAddress: {
                fullAddress: "",
                street_number: "",
                route: "",
                locality: "",
                administrative_area_level_1: "",
                country: "",
                postal_code: "",
                latitude: 0,
                longitude: 0,
                placeId: ""
            },
            siteAddressDoesntExist: false,
            errors: {},
            loading: false
        }
    }

    search = (e: any) => {
        e.preventDefault();

        var jobIdValid = true;
        var jobId = this.state.jobIdSearch;
        if (jobId) {
            var checkJobId = Number(jobId);
            if (Number.isNaN(checkJobId)) {
                this.setState({ showResults: false });
                toast.error("Please enter a valid Job No!");
                jobIdValid = false;
                return;
            }
        } else {
            jobIdValid = false;     //no job id entered
        }

        //check to make sure at least one search field has been entered
        if (this.state.jobIdSearch || this.state.siteAddressSearch) {
            this.searchJobs();
        } else {
            this.setState({ showResults: false });
            toast.error("Please enter at least one search item");
        }

    }

    searchJobs = async () => {
        const apiUrl = "SearchJobs?JobId=" + this.state.jobIdSearch + '&CustomerId=&QuoteNo=&SiteAddress=' + encodeURIComponent(this.state.siteAddressSearch) + '&Suburb=&SiteVisit=&StatusId=-1&ShowCancelledJobs=false';
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            this.setState({ showResults: true, results: result.data });
        } 
    }

    selectJob = (jobId: number, siteAddress: string, customer: string) => {
        var errors = this.state.errors;
        delete errors["jobId"];

        //load new invoice based on job selected
        this.setState({ jobIdSearch: "", siteAddressSearch: "", showResults: false, results: [], errors: errors });

        let item = this.props.newItem;
        item.jobId = jobId;
        item.siteAddress = siteAddress;
        item.customerName = customer;
        this.props.updateData(item);
    }

    handleChange = (e: any) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let change: any = {};
        change[e.target.name] = e.target.value;
        change["errors"] = errors;
        this.setState(change);
    }

    handleAddressExistsChange = (e: any) => {
        var errors = this.state.errors;
        delete errors["siteAddress"];
        this.setState({ errors: errors, siteAddressDoesntExist: e.target.checked });
    }

    handlePropsChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let newItem: any = this.props.newItem;
        newItem[e.target.name] = e.target.value;

        //update errors
        let change: any = {};
        change["errors"] = errors;
        this.setState(change);

        this.props.updateData(newItem);
    }

    handlePropsNumberChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let newItem: any = this.props.newItem;
        newItem[e.target.name] = e.target.value * 1;

        //update errors
        let change: any = {};
        change["errors"] = errors;
        this.setState(change);

        this.props.updateData(newItem);
    }


    handlePropsSchedulerChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let newItem: any = this.props.newItem;
        let value = e.target.value * 1;
        newItem[e.target.name] = value;

        if (this.props.systemCompanyId === 3 && value != 4) {
            //need to set type to Other
            newItem.typeId = 5;
        } else {
            newItem.typeId = -1;
        }

        //update errors
        let change: any = {};
        change["errors"] = errors;
        this.setState(change);

        this.props.updateData(newItem);
    }

    setAddress = async (updatedAddress: AddressDetails) => {
        let item = this.props.newItem;
        item.googleAddress = updatedAddress.fullAddress;
        item.googleLatitude = updatedAddress.latitude;
        item.googleLongitude = updatedAddress.longitude;
        this.props.updateData(item);
    }

    hideModal = () => {
        this.setState({ errors: {} });
        this.props.hideModal();
    }

    validate = () => {
        let fields = this.props.newItem;
        let errors: any = {};
        let formIsValid = true;

        var siteAddr = this.state.siteAddressDoesntExist ? fields.siteAddress : fields.googleAddress;

        if (fields.jobId === 0 && !siteAddr) {
            formIsValid = false;
            errors["siteAddress"] = "Site Address is required";
        }

        if (!fields.customerName) {
            formIsValid = false;
            errors["customerName"] = "Customer Name is required";
        }

        if (!fields.description) {
            formIsValid = false;
            errors["description"] = "Description is required";
        }

        if (fields.typeId <= 0) {
            formIsValid = false;
            errors["typeId"] = "Type is required";
        }

        if (this.props.systemCompanyId === 3 && fields.itemTypeGroupId <= 0) {
            formIsValid = false;
            errors["itemTypeGroupId"] = "Item Type Group is required";
        }

        this.setState({ errors: errors });
        return formIsValid;
    }

    save = (e: any) => {
        e.preventDefault();

        if (this.validate()) {
            this.props.save();
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return (<div>
            {contents}
        </div>);
    }

    renderDetails = () => {
        let newItem = this.props.newItem;
        let scheduleTypes = this.props.scheduleTypes;
        let itemTypeGroups = this.props.itemTypeGroups;

        var googleContents = <GooglePlacesDropdown addressId="autocompletemanual" addressLabel={"Site Address"} placeholder={newItem.googleAddress} className="input-group" useLargeLabel={false} afterValid={this.setAddress} />
        return (
            <div className="static-modal">
                <Modal size="lg" show={this.props.showNewItemModal} onHide={this.hideModal}>
                    <Modal.Header className="modal__header--colour">
                        <Modal.Title>Add new manual item</Modal.Title>
                        <button type="button" className="close" onClick={this.hideModal}>&times;</button>
                    </Modal.Header>
                    <Modal.Body>
                        <div className={this.props.driverJob ? "hidden" : "makeitflex"}>
                            <label className="input-group--inline" htmlFor="jobIdSearch">
                                <span className="label">Job Id</span>
                                <input className="form-control allocatenewJobId" type="text" id="jobIdSearch" name="jobIdSearch" value={this.state.jobIdSearch} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <label className="input-group--inline" htmlFor="siteAddressSearch">
                                <span className="label label--setsizesm">Site Address</span>
                                <input className="form-control allocateSiteAddress" type="text" id="siteAddressSearch" name="siteAddressSearch" value={this.state.siteAddressSearch} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <button className="defaultbutton allocate__searchMargin" type='button' onClick={this.search}>Search</button>
                        </div>
                        <div className={this.state.showResults ? "" : "hidden"}>
                            <div className={this.state.results.length > 0 ? 'overflowAuto' : 'hidden'}>
                                <table className='table tableColours'>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>Job Id</th>
                                            <th>Site Address</th>
                                            <th>Customer</th>
                                            <th>Quote Number</th>
                                            <th>Status</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.results.map(job =>
                                            <tr key={job.id}>
                                                <td><input type='checkbox' className="input" checked={false} onChange={() => this.selectJob(job.id, job.siteAddress, job.customer)} /></td>
                                                <td>{job.id}</td>
                                                <td>{job.siteAddress}</td>
                                                <td>{job.customer}</td>
                                                <td>{job.finalQuoteNumber}</td>
                                                <td>{job.status}</td>
                                                <td>
                                                    <Link to={'/job/' + job.id + '/details'} target="_blank" >
                                                        Open
                                                    </Link>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <span className={this.state.results.length == 0 ? "" : "hidden"}>There were no matching jobs found. Try modifying your search criteria.</span>
                        </div>
                        <div className="row">
                            <hr></hr>
                        </div>
                        <label className={this.props.driverJob ? "hidden" : "input-group"} htmlFor="jobId">
                            <span className="label">Job Id</span>
                            <input type='text' id="jobId" name="jobId" className='input allocate__size100' value={newItem.jobId} onChange={(e) => this.handlePropsChange(e)} disabled></input>
                        </label>
                        {/*<label className={this.state.siteAddressDoesntExist || newItem.jobId > 0 ? "hidden" : "input-group"} htmlFor="googleAddress">*/}
                        {/*    {googleContents}*/}
                        {/*</label>*/}
                        <div className={this.state.siteAddressDoesntExist || newItem.jobId > 0 ? "hidden" : ""}>
                            {googleContents}
                        </div>
                        <label className={newItem.jobId === 0 ? "input-group" : "hidden"} htmlFor="siteAddressDoesntExist">
                            <span className="label label--setsizesm">Site Address Doesn't Exist</span>
                            <input className="checkbox" type="checkbox" id="siteAddressDoesntExist" name="siteAddressDoesntExist" checked={this.state.siteAddressDoesntExist} onChange={(e) => this.handleAddressExistsChange(e)}></input>
                        </label>
                        <label className={this.state.siteAddressDoesntExist || newItem.jobId > 0 ? "input-group" : "hidden"} htmlFor="siteAddress">
                            <span className="label">Site Address</span>
                            <input type='text' id="siteAddress" name="siteAddress" className='input' value={newItem.siteAddress} onChange={(e) => this.handlePropsChange(e)}></input>
                        </label>
                        <span className={this.state.errors["siteAddress"] ? "label marginLeftLabel errors" : "hidden"}>{this.state.errors["siteAddress"]}</span>
                        <label className="input-group" htmlFor="customerName">
                            <span className="label">{this.props.driverJob ? "Company" : "Customer Name"}</span>
                            <input type='text' id="customerName" name="customerName" className='input' value={newItem.customerName} onChange={(e) => this.handlePropsChange(e)}></input>
                        </label>
                        <span className={this.state.errors["customerName"] ? "label marginLeftLabel errors" : "hidden"}>{this.state.errors["customerName"]}</span>
                        <label className="input-group" htmlFor="contactName">
                            <span className="label">{this.props.driverJob ? "Name / Phone" : "Contact Name / Phone"}</span>
                            <input type='text' id="contactName" name="contactName" className='input' value={newItem.contactName} onChange={(e) => this.handlePropsChange(e)}></input>
                        </label>
                        <label className="input-group" htmlFor="description">
                            <span className="label">Description</span>
                            <input type='text' id="description" name="description" className='input' value={newItem.description} onChange={(e) => this.handlePropsChange(e)}></input>
                        </label>
                        <span className={this.state.errors["description"] ? "label marginLeftLabel errors" : "hidden"}>{this.state.errors["description"]}</span>
                        <label className={this.props.systemCompanyId === 3 ? "input-group" : "hidden"} htmlFor="itemTypeGroupId">
                            <span className="label">Scheduler</span>
                            <select className="select" id="itemTypeGroupId" name="itemTypeGroupId" value={newItem.itemTypeGroupId} onChange={(e) => this.handlePropsSchedulerChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {itemTypeGroups.map(group =>
                                    <option key={group.id} value={group.id}>{group.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={this.state.errors["itemTypeGroupId"] ? "label marginLeftLabel errors" : "hidden"}>{this.state.errors["itemTypeGroupId"]}</span>
                        <label className="input-group" htmlFor="typeId">
                            <span className="label">Type</span>
                            <select className="select" id="typeId" name="typeId" value={newItem.typeId} onChange={(e) => this.handlePropsNumberChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {scheduleTypes.map(type =>
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={this.state.errors["typeId"] ? "label marginLeftLabel errors" : "hidden"}>{this.state.errors["typeId"]}</span>
                        <label className={this.props.driverJob ? "hidden" : "input-group"} htmlFor="notes">
                            <span className="label">Schedule Notes</span>
                            <textarea className="input allocate__teamnotes notesSize" id="notes" name="notes" value={newItem.notes} onChange={(e) => this.handlePropsChange(e)}></textarea>
                        </label>
                        <label className="input-group" htmlFor="runsheetNotes">
                            <span className="label">{this.props.driverJob ? "Notes" : "Runsheet Notes"}</span>
                            <textarea className="input allocate__teamnotes notesSize marginTop10" id="runsheetNotes" name="runsheetNotes" value={newItem.runsheetNotes} onChange={(e) => this.handlePropsChange(e)}></textarea>
                        </label>
                    </Modal.Body>

                    <Modal.Footer>
                        <button className="popup-button--yes" onClick={this.save}>Save</button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }



}